// apps/web/src/pages/confirm-email-change/[token].tsx — Quick-Task 260920-u3p.
//
// Public deeplink page for confirming an admin email change. The
// page:
//   - reads the token from the URL path
//   - fires the ConfirmAdminEmailChangePage mutation ONCE on mount
//   - renders one of three states: pending, success, error
//   - on success: offers a button that deep-links into the admin
//     login (path-only — no secret in the redirect URL)
//   - on error: offers a button back to the admin login
//
// Why public (no JWT, no CSRF)? Per the plan + CONTEXT.md BLOCKER-4
// the token IS the credential. The mutation is bound by:
//   - Argon2id PHC + sha256 lookup digest + single-use status flip
//   - 24h TTL
//   - The resolver deliberately does NOT distinguish invalid-token
//     from already-consumed (so the deeplink cannot be used as an
//     oracle).
//
// XSRF is intentionally NOT required here — the deeplink is reached
// only by clicking a link inside a magic-link mail. The mutation is
// POST-shaped, the server is the same origin (gqlRequest hits
// /graphql), and the actual credential is the token itself. Adding
// CSRF on top would defeat the magic-link ergonomics without
// improving security (the token already gates everything).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { gqlRequest } from "../../lib/api";
import { CONFIRM_ADMIN_EMAIL_CHANGE_MUTATION } from "./confirm-email-change-operations";

interface ConfirmSuccessResponse {
  confirmAdminEmailChange: {
    user: { id: string; email: string; role: string };
    restaurant: { id: string; slug: string; name: string };
  };
}

type Status = "pending" | "success" | "error";

export function ConfirmEmailChangePage(): JSX.Element {
  const { t } = useTranslation();
  const params = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("pending");
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (token: string) =>
      gqlRequest<ConfirmSuccessResponse>(CONFIRM_ADMIN_EMAIL_CHANGE_MUTATION, {
        token,
      }),
  });

  // Fire ONCE on mount. We intentionally don't allow a re-submit
  // from the UI: a retry would hit the resolver's single-use guard
  // and produce the same SELF_SERVICE_TOKEN_INVALID error, but with
  // a worse UX (the operator would think the first attempt failed).
  useEffect(() => {
    const token = params.token;
    if (!token || token.length < 16 || token.length > 256) {
      setStatus("error");
      setErrorCode("SELF_SERVICE_TOKEN_INVALID");
      return;
    }
    mutation.mutate(token, {
      onSuccess: (res) => {
        setStatus("success");
        setConfirmedEmail(res.confirmAdminEmailChange.user.email);
      },
      onError: (err) => {
        if (err instanceof ClientError) {
          const code = err.response?.errors?.[0]?.extensions?.code as
            string | undefined;
          setErrorCode(code ?? "INTERNAL_SERVER_ERROR");
        } else {
          setErrorCode("INTERNAL_SERVER_ERROR");
        }
        setStatus("error");
      },
    });
    // mutation is referentially stable per useMutation contract; we
    // intentionally depend ONLY on the token so the network call
    // fires exactly once even if React re-creates the mutation
    // object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.token]);

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex items-center justify-center px-4"
      data-testid="confirm-email-change-page"
    >
      <div className="w-full max-w-md space-y-6 text-center">
        {status === "pending" && <PendingView t={t} />}

        {status === "success" && confirmedEmail && (
          <SuccessView
            email={confirmedEmail}
            t={t}
            onContinue={() => navigate("/admin/login")}
          />
        )}

        {status === "error" && <ErrorView code={errorCode} t={t} />}
      </div>
    </main>
  );
}

function PendingView({ t }: { t: ReturnType<typeof useTranslation>["t"] }) {
  return (
    <>
      <h1
        className="font-heading text-2xl md:text-3xl"
        data-testid="confirm-email-change-title"
      >
        {t("accountConfirm.title")}
      </h1>
      <p
        className="text-text/70"
        data-testid="confirm-email-change-status"
        role="status"
      >
        {t("accountConfirm.pending")}
      </p>
    </>
  );
}

function SuccessView({
  email,
  t,
  onContinue,
}: {
  email: string;
  t: ReturnType<typeof useTranslation>["t"];
  onContinue: () => void;
}) {
  return (
    <>
      <h1
        className="font-heading text-2xl md:text-3xl"
        data-testid="confirm-email-change-title"
      >
        {t("accountConfirm.successTitle")}
      </h1>
      <p
        className="text-text/70"
        data-testid="confirm-email-change-success"
        role="status"
      >
        {t("accountConfirm.successBody", { email })}
      </p>
      <p className="text-sm text-text/70">{t("accountConfirm.successHint")}</p>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onContinue}
        data-testid="confirm-email-change-continue"
      >
        {t("accountConfirm.continue")}
      </button>
    </>
  );
}

function ErrorView({
  code,
  t,
}: {
  code: string | null;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  // Deliberately do NOT distinguish invalid-token from expired or
  // already-consumed. The resolver already collapsed those into a
  // single code (SELF_SERVICE_TOKEN_INVALID). Showing different UI
  // would leak which case occurred.
  const isRateLimit = code === "ADMIN_EMAIL_CHANGE_RATE_LIMITED";
  return (
    <>
      <h1
        className="font-heading text-2xl md:text-3xl"
        data-testid="confirm-email-change-title"
      >
        {t(
          isRateLimit
            ? "accountConfirm.errorRateLimitTitle"
            : "accountConfirm.errorTitle",
        )}
      </h1>
      <p
        className="text-text/70"
        data-testid="confirm-email-change-error"
        role="alert"
      >
        {t(
          isRateLimit
            ? "accountConfirm.errorRateLimitBody"
            : "accountConfirm.errorBody",
        )}
      </p>
      <a
        href="/admin/login"
        className="btn btn-secondary inline-block"
        data-testid="confirm-email-change-back"
      >
        {t("accountConfirm.backToLogin")}
      </a>
    </>
  );
}
